import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'themes';
import {
  TermsKindCode as TermsKindCodeFrism,
  useGetApiContentsTerms,
} from 'store';
import '@toast-ui/editor/dist/toastui-editor-viewer.css';
import { Viewer } from '@toast-ui/react-editor';
import tableMergedCellPlugin from '@toast-ui/editor-plugin-table-merged-cell';
import { useLocation } from 'react-router';
import queryString from 'query-string';
import SelectBox from 'pages/TermsDetail/SelectBox';
import useNavigateSearch from 'hooks/useNavigateSearch';
import { TermsKindCode, useGetApiTerms } from 'src/api/remoteStore';
import RnEventHandler from 'src/utils/RnEventHandler';

type ScrollContainer = Window | HTMLElement;
type ScrollMetrics = {
  scrollTop: number;
  viewportHeight: number;
  scrollHeight: number;
};

const BOTTOM_TOLERANCE_PX = 2;
const isWindowContainer = (container: ScrollContainer): container is Window =>
  container === window;

const getScrollContainer = (): ScrollContainer => {
  const rootElement = document.getElementById('root');

  if (!rootElement) {
    return window;
  }

  const rootStyle = window.getComputedStyle(rootElement);
  const isRootScrollable =
    (rootStyle.overflowY === 'scroll' || rootStyle.overflowY === 'auto') &&
    rootElement.scrollHeight > rootElement.clientHeight;

  return isRootScrollable ? rootElement : window;
};

const getViewportHeight = (container: ScrollContainer): number => {
  if (isWindowContainer(container)) {
    return window.visualViewport?.height ?? window.innerHeight;
  }

  return container.clientHeight;
};

const getScrollMetrics = (container: ScrollContainer): ScrollMetrics => {
  if (isWindowContainer(container)) {
    const docEl = document.documentElement;
    const body = document.body;
    return {
      scrollTop: window.pageYOffset || docEl.scrollTop || body.scrollTop || 0,
      viewportHeight: getViewportHeight(window),
      scrollHeight: Math.max(docEl.scrollHeight, body.scrollHeight),
    };
  }

  return {
    scrollTop: container.scrollTop,
    viewportHeight: getViewportHeight(container),
    scrollHeight: container.scrollHeight,
  };
};

const isScrolledToBottom = ({
  scrollTop,
  viewportHeight,
  scrollHeight,
}: ScrollMetrics): boolean =>
  scrollTop + viewportHeight >= scrollHeight - BOTTOM_TOLERANCE_PX;

const scrollToTop = (container: ScrollContainer) => {
  if (isWindowContainer(container)) {
    window.scrollTo({ top: 0, behavior: 'auto' });
    return;
  }

  container.scrollTo({ top: 0, behavior: 'auto' });
};

const TermsDetail = () => {
  const canInit = useRef(true);
  const scrollContainerRef = useRef<ScrollContainer | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const navigateSearch = useNavigateSearch();
  const location = useLocation();
  const [isAtBottom, setIsAtBottom] = useState(false);

  const params = location.pathname.split('/');

  const query = (
    location.search
      ? queryString.parse(location.search)
      : {
          type: '전체',
          termsId: undefined,
          showAgreeButton: undefined,
        }
  ) as {
    type: TermsKindCodeFrism;
    termsId: string | undefined;
    showAgreeButton?: string;
  };
  const shouldShowAgreeButton = query.showAgreeButton === 'true';

  const isCrm = params.includes('crm');
  const queryOption = {
    query: { cacheTime: 0 },
  };

  const { data } = isCrm
    ? useGetApiTerms(
        {
          termsKindCode: query.type as TermsKindCode | undefined,
          termsId: query.termsId ? +query.termsId : undefined,
        },
        queryOption,
      )
    : useGetApiContentsTerms(
        {
          termsKindCode: query.type as TermsKindCodeFrism,
          termsId: query.termsId ? +query.termsId : undefined,
        },
        queryOption,
      );

  const urlContent = data?.result?.termsContent?.startsWith('https://')
    ? data.result.termsContent
    : null;
  const canShowViewer = Boolean(data?.result && !urlContent);

  useEffect(() => {
    if (urlContent) {
      window.location.replace(urlContent);
    }
  }, [urlContent]);

  const termsData = Object.entries(data?.result?.dateList ?? {}).map(
    ([key, value]) => {
      return { date: key, termsId: value };
    },
  );

  const onSelect = (termsId: number) => {
    navigateSearch('/detail/', {
      type: query.type,
      termsId: `${termsId}`,
    });
  };

  const updateBottomState = useCallback(() => {
    const container = scrollContainerRef.current ?? getScrollContainer();
    scrollContainerRef.current = container;
    setIsAtBottom(isScrolledToBottom(getScrollMetrics(container)));
  }, []);

  const scheduleBottomStateUpdate = useCallback(() => {
    if (scrollRafRef.current !== null) {
      return;
    }

    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;
      updateBottomState();
    });
  }, [updateBottomState]);

  useEffect(() => {
    if (!canShowViewer || !shouldShowAgreeButton) {
      return;
    }

    const container = getScrollContainer();
    scrollContainerRef.current = container;

    const onScroll = () => {
      scheduleBottomStateUpdate();
    };
    const onResize = () => {
      updateBottomState();
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    updateBottomState();

    return () => {
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
      if (scrollRafRef.current !== null) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [
    canShowViewer,
    scheduleBottomStateUpdate,
    shouldShowAgreeButton,
    updateBottomState,
  ]);

  useEffect(() => {
    if (
      termsData.length > 0 &&
      canInit.current &&
      (query.termsId === undefined || query.termsId === '')
    ) {
      canInit.current = false;
      navigateSearch('/detail/', {
        type: query.type,
        termsId: `${termsData[0].termsId}`,
      });
    }
  }, [navigateSearch, query.termsId, query.type, termsData]);

  useEffect(() => {
    if (!canShowViewer || !shouldShowAgreeButton) {
      return;
    }

    const container = getScrollContainer();
    scrollContainerRef.current = container;
    setIsAtBottom(false);
    scrollToTop(container);
    updateBottomState();
  }, [
    canShowViewer,
    query.termsId,
    data?.result?.termsContent,
    shouldShowAgreeButton,
    updateBottomState,
  ]);

  const onFloatingButtonClick = useCallback(() => {
    const container = scrollContainerRef.current ?? getScrollContainer();
    scrollContainerRef.current = container;

    if (isAtBottom) {
      const payload = {
        termsId: query.termsId ? Number(query.termsId) : undefined,
        termsKindCode: query.type,
        isAgree: true as const,
        source: 'terms-detail' as const,
      };

      if (window.ReactNativeWebView) {
        RnEventHandler.query('terms-detail-confirm', payload);
      }

      return;
    }

    const viewportHeight = getViewportHeight(container);
    if (isWindowContainer(container)) {
      window.scrollBy({ top: viewportHeight, behavior: 'smooth' });
      return;
    }

    container.scrollBy({ top: viewportHeight, behavior: 'smooth' });
  }, [isAtBottom, query.termsId, query.type]);

  return (
    <Container>
      <SelectBox data={termsData} onSelect={onSelect} query={query} />

      {canShowViewer && (
        <Viewer
          initialValue={data?.result?.termsContent ?? ''}
          plugins={[tableMergedCellPlugin]}
          linkAttributes={{ target: '_blank', rel: 'noopener noreferrer' }}
        />
      )}
      {canShowViewer && shouldShowAgreeButton && (
        <FloatingButton type="button" onClick={onFloatingButtonClick}>
          {isAtBottom ? '모든 약관을 확인하였습니다' : '스크롤해서 확인하기'}
        </FloatingButton>
      )}
    </Container>
  );
};

export default TermsDetail;

const Container = styled.div`
  padding: 20px 20px 124px 20px;

  /* NOTE: kunhee.lim
   *  table 표 좌측 글자 최소 영역 확보  */
  table tbody tr td:first-child {
    min-width: 90px;
  }
`;

const FloatingButton = styled.button`
  z-index: 10;
  position: fixed;
  left: 20px;
  right: 20px;
  bottom: calc(40px + env(safe-area-inset-bottom));
  height: 48px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.buttonPrimarySurface};
  color: ${({ theme }) => theme.colors.buttonPrimaryText};
  ${({ theme }) => theme.text.buttonNormal};
`;
